import { useState, useEffect } from 'react'
import type { AiProvider, AiConfig } from '../lib/types'

interface ApiSettingsProps {
  onConfigChange: (config: AiConfig | null) => void
  disabled?: boolean
}

const PROVIDERS: { value: AiProvider; label: string; placeholder: string; emoji: string }[] = [
  { value: 'openai', label: 'OpenAI', placeholder: 'sk-...', emoji: '🧠' },
  { value: 'gemini', label: 'Gemini', placeholder: 'AIza...', emoji: '✨' },
  { value: 'claude', label: 'Claude', placeholder: 'sk-ant-...', emoji: '🤖' },
]

const STORAGE_KEY = 'vet-symptom-ai-config'

export default function ApiSettings({ onConfigChange, disabled }: ApiSettingsProps) {
  const [provider, setProvider] = useState<AiProvider>('openai')
  const [apiKey, setApiKey] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)
  const [isExpanded, setIsExpanded] = useState(true)

  // ローカルストレージから設定を読み込み
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        const config = JSON.parse(saved) as AiConfig
        setProvider(config.provider)
        setApiKey(config.apiKey)
        onConfigChange(config)
        setIsExpanded(false)
      } catch {
        // ignore
      }
    }
  }, [])

  // 設定変更時に通知
  useEffect(() => {
    if (apiKey.trim()) {
      const config: AiConfig = { provider, apiKey: apiKey.trim() }
      onConfigChange(config)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config))
    } else {
      onConfigChange(null)
    }
  }, [provider, apiKey, onConfigChange])

  const currentProvider = PROVIDERS.find((p) => p.value === provider)

  return (
    <div className="bg-white/90 dark:bg-dark-bg-card/90 backdrop-blur-sm rounded-3xl shadow-cute border-2 border-light-paw/30 dark:border-dark-paw/30 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-light-paw/20 dark:hover:bg-dark-paw/20 transition-all duration-300"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{apiKey ? '🔐' : '🔑'}</span>
          <div>
            <h3 className="font-bold text-light-text dark:text-dark-text flex items-center gap-2">
              AI設定
              {apiKey && !isExpanded && (
                <span className="text-xs bg-light-success/20 dark:bg-dark-success/20 text-light-success dark:text-dark-success px-2 py-0.5 rounded-full">
                  設定済み
                </span>
              )}
            </h3>
            {apiKey && !isExpanded && (
              <p className="text-sm text-light-text-sub dark:text-dark-text-sub flex items-center gap-1">
                {currentProvider?.emoji} {currentProvider?.label}
              </p>
            )}
          </div>
        </div>
        <div
          className={`w-8 h-8 rounded-full bg-light-bg-sub dark:bg-dark-bg-sub flex items-center justify-center transition-transform duration-300 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        >
          <span className="text-light-text-sub dark:text-dark-text-sub">▼</span>
        </div>
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 space-y-5 border-t-2 border-dashed border-light-paw/30 dark:border-dark-paw/30">
          {/* AIプロバイダー選択 */}
          <div className="pt-5">
            <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-3 flex items-center gap-2">
              <span>🤖</span>
              AIプロバイダー
            </label>
            <div className="grid grid-cols-3 gap-3">
              {PROVIDERS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  disabled={disabled}
                  onClick={() => setProvider(p.value)}
                  className={`
                    p-3 rounded-2xl font-medium transition-all duration-300
                    flex flex-col items-center gap-1
                    ${
                      provider === p.value
                        ? 'bg-gradient-to-br from-light-accent to-pet-coral dark:from-dark-accent dark:to-pet-coral text-white shadow-cute scale-105'
                        : 'bg-light-bg-sub dark:bg-dark-bg-sub text-light-text dark:text-dark-text hover:bg-light-paw/30 dark:hover:bg-dark-paw/20'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  <span className={`text-2xl ${provider === p.value ? 'animate-wiggle' : ''}`}>
                    {p.emoji}
                  </span>
                  <span className="text-xs">{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* APIキー入力 */}
          <div>
            <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-3 flex items-center gap-2">
              <span>🔑</span>
              APIキー
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={currentProvider?.placeholder}
                disabled={disabled}
                className="
                  w-full px-5 py-4 pr-14 rounded-2xl
                  border-2 border-light-paw/30 dark:border-dark-paw/30
                  bg-white dark:bg-dark-bg-sub
                  text-light-text dark:text-dark-text
                  placeholder-light-text-sub/50 dark:placeholder-dark-text-sub/50
                  focus:border-light-accent dark:focus:border-dark-accent
                  focus:ring-4 focus:ring-light-accent/20 dark:focus:ring-dark-accent/20
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-all duration-300
                "
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl hover:scale-110 transition-transform"
              >
                {showApiKey ? '🙈' : '👁️'}
              </button>
            </div>
            <div className="mt-3 bg-pet-lavender/30 dark:bg-pet-lavender/10 rounded-xl p-3">
              <p className="text-xs text-light-text-sub dark:text-dark-text-sub flex items-start gap-2">
                <span>💡</span>
                <span>
                  APIキーはブラウザに保存されるよ。サーバーには要約時のみ送信されます。
                </span>
              </p>
            </div>
          </div>

          {/* クリアボタン */}
          {apiKey && (
            <button
              type="button"
              onClick={() => {
                setApiKey('')
                localStorage.removeItem(STORAGE_KEY)
                onConfigChange(null)
              }}
              disabled={disabled}
              className="
                text-sm text-light-error dark:text-dark-error
                hover:underline
                disabled:opacity-50
                flex items-center gap-1
              "
            >
              <span>🗑️</span>
              APIキーをクリア
            </button>
          )}
        </div>
      )}
    </div>
  )
}
